// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Calendar as CalendarIcon,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Scale,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { MobilePageContainer } from '../ui/MobilePageContainer';
import { MobileStatCard } from '../ui/MobileStatCard';
import { MobileListItem } from '../ui/MobileListItem';
import { MobileEmptyState } from '../ui/MobileEmptyState';
import { MobileSearchBar } from '../ui/MobileSearchBar';
import { MobileFab } from '../ui/MobileFab';
import { Button } from '@/components/ui/button';
import MobileFinancialQuickForm from './MobileFinancialQuickForm';

type Entry = {
  id: string;
  description: string;
  amount: number;
  entry_type: string;
  category?: string;
  due_date?: string;
  payment_date?: string;
  status?: string;
  payment_confirmed?: boolean;
  case_id?: string | null;
  clients?: { name?: string } | null;
  cases?: { title?: string } | null;
  is_installment?: boolean;
  installments_count?: number;
};

type Tab = 'pending' | 'received' | 'paid' | 'all';

const isIncome = (t: string) =>
  t === 'income' || t === 'receita' || t === 'revenue';

const fmtMoney = (n: number) =>
  (Number(n) || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const fmtDate = (d?: string) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: 'short',
  });
};

const isOverdue = (due?: string, paid?: string, confirmed?: boolean) => {
  if (paid || confirmed) return false;
  if (!due) return false;
  return new Date(due).getTime() < Date.now();
};

const MobileFinancial = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('pending');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('financial_entries')
        .select(
          'id, description, amount, entry_type, category, due_date, payment_date, status, payment_confirmed, case_id, is_installment, installments_count, clients(name), cases(title)',
        )
        .eq('user_id', user.id)
        .order('due_date', { ascending: false, nullsFirst: false })
        .limit(500);
      if (error) throw error;
      setEntries(data || []);
    } catch (err: any) {
      toast({
        title: 'Erro ao carregar lançamentos',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const summary = useMemo(() => {
    let toReceive = 0;
    let toPay = 0;
    let receivedMonth = 0;
    let paidMonth = 0;
    let overdue = 0;

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    entries.forEach((e) => {
      const amt = Number(e.amount) || 0;
      const paid = !!e.payment_date || !!e.payment_confirmed;
      if (!paid) {
        if (isIncome(e.entry_type)) toReceive += amt;
        else toPay += amt;
        if (isOverdue(e.due_date, e.payment_date, e.payment_confirmed)) {
          overdue += amt;
        }
      } else {
        const pd = e.payment_date ? new Date(e.payment_date) : null;
        if (pd && pd >= monthStart) {
          if (isIncome(e.entry_type)) receivedMonth += amt;
          else paidMonth += amt;
        }
      }
    });

    return { toReceive, toPay, receivedMonth, paidMonth, overdue };
  }, [entries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      const paid = !!e.payment_date || !!e.payment_confirmed;
      if (tab === 'pending' && paid) return false;
      if (tab === 'received' && !(paid && isIncome(e.entry_type))) return false;
      if (tab === 'paid' && !(paid && !isIncome(e.entry_type))) return false;

      if (!q) return true;
      return [e.description, e.category, e.clients?.name, e.cases?.title]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [entries, tab, search]);

  return (
    <MobilePageContainer>
      {/* Resumo */}
      <div className="grid grid-cols-2 gap-2 px-3 pt-3 mb-2">
        <MobileStatCard
          label="A receber"
          value={fmtMoney(summary.toReceive)}
          icon={<TrendingUp className="h-4 w-4" />}
          tone="success"
        />
        <MobileStatCard
          label="A pagar"
          value={fmtMoney(summary.toPay)}
          icon={<TrendingDown className="h-4 w-4" />}
          tone="danger"
        />
        <MobileStatCard
          label="Recebido no mês"
          value={fmtMoney(summary.receivedMonth)}
          icon={<CheckCircle2 className="h-4 w-4" />}
          tone="info"
        />
        <MobileStatCard
          label="Em atraso"
          value={fmtMoney(summary.overdue)}
          icon={<AlertTriangle className="h-4 w-4" />}
          tone="warning"
        />
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="px-3 pt-2 pb-2">
          <MobileSearchBar
            value={search}
            onChange={setSearch}
            placeholder="Buscar lançamento..."
          />
        </div>
        <div className="px-3 pb-2 flex gap-2 overflow-x-auto scrollbar-hide">
          {[
            { id: 'pending' as Tab, label: 'Pendentes' },
            { id: 'received' as Tab, label: 'Recebidos' },
            { id: 'paid' as Tab, label: 'Pagos' },
            { id: 'all' as Tab, label: 'Todos' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium border shrink-0 transition-colors',
                tab === t.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="px-4 py-6 text-sm text-muted-foreground text-center">
          Carregando...
        </div>
      ) : filtered.length === 0 ? (
        <MobileEmptyState
          className="mt-8"
          icon={<Wallet className="h-6 w-6" />}
          title="Nenhum lançamento"
          description={
            search
              ? 'Tente outra busca.'
              : 'Toque em + para criar um lançamento.'
          }
          action={
            !search && (
              <Button onClick={() => setCreateOpen(true)}>Novo lançamento</Button>
            )
          }
        />
      ) : (
        <div className="divide-y divide-border bg-card">
          {filtered.map((e) => {
            const paid = !!e.payment_date || !!e.payment_confirmed;
            const overdue = isOverdue(
              e.due_date,
              e.payment_date,
              e.payment_confirmed,
            );
            const income = isIncome(e.entry_type);

            return (
              <MobileListItem
                key={e.id}
                icon={
                  income ? (
                    <TrendingUp className="h-5 w-5" />
                  ) : (
                    <TrendingDown className="h-5 w-5" />
                  )
                }
                iconBg={
                  income
                    ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                    : 'bg-rose-500/15 text-rose-600 dark:text-rose-400'
                }
                title={e.description || '(sem descrição)'}
                subtitle={
                  <>
                    {e.clients?.name && <span>{e.clients.name}</span>}
                    {e.clients?.name && e.cases?.title && <span> · </span>}
                    {e.cases?.title && (
                      <span className="inline-flex items-center gap-1">
                        <Scale className="h-3 w-3" /> {e.cases.title}
                      </span>
                    )}
                    {!e.clients?.name && !e.cases?.title && e.category && (
                      <span>{e.category}</span>
                    )}
                  </>
                }
                meta={
                  <span
                    className={cn(
                      'font-bold text-[13px]',
                      income ? 'text-emerald-600' : 'text-rose-600',
                    )}
                  >
                    {income ? '+' : '-'} {fmtMoney(e.amount)}
                  </span>
                }
                badges={
                  <>
                    {paid ? (
                      <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-0 text-[10px]">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {e.payment_date
                          ? fmtDate(e.payment_date)
                          : 'Confirmado'}
                      </Badge>
                    ) : overdue ? (
                      <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-300 border-0 text-[10px]">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Atrasado · {fmtDate(e.due_date)}
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border-0 text-[10px]">
                        <Clock className="h-3 w-3 mr-1" />
                        Vence {fmtDate(e.due_date)}
                      </Badge>
                    )}
                    {e.is_installment && e.installments_count && (
                      <Badge className="bg-sky-500/15 text-sky-700 dark:text-sky-300 border-0 text-[10px]">
                        {e.installments_count}x
                      </Badge>
                    )}
                  </>
                }
              />
            );
          })}
        </div>
      )}

      <MobileFab onClick={() => setCreateOpen(true)} label="Novo" />

      <MobileFinancialQuickForm
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => load()}
      />
    </MobilePageContainer>
  );
};

export default MobileFinancial;
